import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import tw from 'twin.macro'
import { makeStyles } from '@material-ui/core/styles'

import Copy from '@objects/copy'
import Headline from '@objects/headline'
import Icon from '@objects/icon'
import Carousel from '@objects/carousel'

import Img from 'gatsby-image'
import clsx from 'clsx'
import { useTheme } from '@hooks'

const Swiper = styled(Carousel)`
  .swiper-slide {
    transition-property: none;
    &-prev,
    &-duplicate-prev,
    &.swiper-slide-prev {
      transform: none;
    }
    &.swiper-slide-next {
      transform: none;
    }
  }
  .button {
    display: none;
  }
`
const SwiperOverlay = styled(Carousel)`
  .swiper-slide {
    transition-property: none;
    width: 100%;
    margin 0;

    &-prev,
    &-duplicate-prev,
    &.swiper-slide-prev {
      transform: none;
    }
    &.swiper-slide-next {
      transform: none;
    }
  }
  .button {
    display: none;
  }
`

const Lightbox = styled.div`
  ${tw`fixed inset-0 flex items-center justify-center bg-black bg-opacity-25`}
`

const LightboxContent = styled.div`
  ${tw`relative bg-white pt-8 pb-3 px-3 w-full md:w-6/10 lg:w-5/10`}
`

const useStyles = makeStyles((theme) => ({
  renderContainer: {
    width: '520px',

    [theme.breakpoints.down('sm')]: {
      width: '310px',
    },
  },
  image: {
    cursor: 'pointer',
    height: '340px',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      height: '260px',
    },
  },
  renderContainerOverlay: {
    width: 'inherit',
  },
  gradientBase: {
    position: 'absolute',
    height: '80%',
    width: '100%',
    zIndex: 50,
    pointerEvents: 'none',
  },
  gradientLeft: {
    right: '0',
    background:
      'linear-gradient(to left, rgba(255, 255, 255, 0) 90%, rgb(255, 255, 255) 100%)',
  },
  gradientRight: {
    right: '0',
    background:
      'linear-gradient(to right, rgba(255, 255, 255, 0) 90%, rgb(255, 255, 255) 100%)',
  },
  buttonBase: {
    cursor: 'pointer',
    display: 'flex',
    top: '40%',
    transform: 'translateY(-50%)',
    position: 'absolute',
    zIndex: '60',
    borderRadius: '50%',
    backgroundColor: 'rgb(0,56,94)',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    width: '56px',
    height: '56px',
    transition: 'background-color 0.1s ease-in-out',

    '& svg': {
      width: '45px',
      height: '45px',
    },

    '&:hover': {
      backgroundColor: 'rgb(208,218,221)',
      '& svg': {
        color: 'black',
      },
    },

    [theme.breakpoints.down('sm')]: {
      top: '35%',
    },
  },
  buttonRight: {
    right: '-10%',
  },
  buttonLeft: {
    left: '-10%',
  },
  lightbox: {
    zIndex: '65',
  },
}))

function ImageCarousel({ items, headline }) {
  const [isOverlayVisible, setIsOverlayVisible] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const [showPrevButton, setShowPrevButton] = useState(false)
  const [showNextButton, setShowNextButton] = useState(true)
  const classes = useStyles()
  const { screens } = useTheme()
  const swiperRef = useRef(null)
  const swiperRefOverlay = useRef(null)

  const handleImageClick = (itemOrIndex) => {
    const index =
      typeof itemOrIndex === 'number' ? itemOrIndex : items.indexOf(itemOrIndex)
    setSelectedIndex(index)
    setIsOverlayVisible(true)
  }

  const handleCloseLightbox = () => {
    setIsOverlayVisible(false)
  }

  const showButtonCheck = (ref) => {
    setShowPrevButton(ref.current.firstChild.swiper.activeIndex !== 0)
    setShowNextButton(!ref.current.firstChild.swiper.isEnd)
  }

  const handleButton = (ref, dir) => {
    if (ref.current) {
      dir === 'prev'
        ? ref.current.firstChild.swiper.slidePrev()
        : ref.current.firstChild.swiper.slideNext()
      showButtonCheck(ref)
    }
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < parseInt(screens.md))
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [screens])

  return (
    <>
      {headline && (
        <Headline className="text-center" ariaLabel={headline}>
          {headline}
        </Headline>
      )}
      <div className="relative md:py-1">
        {showPrevButton && !isMobile && (
          <div
            className={clsx(classes.buttonBase, classes.buttonLeft)}
            onClick={() => handleButton(swiperRef, 'prev')}
          >
            <Icon name="Prev" />
          </div>
        )}
        {showNextButton && !isMobile && (
          <div
            className={clsx(classes.buttonBase, classes.buttonRight)}
            onClick={() => handleButton(swiperRef, 'next')}
          >
            <Icon name="Next" />
          </div>
        )}
        <div className={clsx(classes.gradientBase, classes.gradientLeft)} />
        <div className={clsx(classes.gradientBase, classes.gradientRight)} />
        {items && items.length > 0 && (
          <div className="relative" ref={swiperRef}>
            <Swiper slidesPerView={'auto'} centeredSlides={true} loop={false}>
              {items.map((item, i) => (
                <div
                  key={`${item.created}${i}`}
                  className={clsx(
                    classes.renderContainer,
                    'my-9 flex align-center'
                  )}
                >
                  <div
                    className="mx-auto my-0 flex flex-col w-full h-full"
                    onClick={() => handleImageClick(item)}
                  >
                    {item.image && (
                      <>
                        <Img
                          alt={item.image.description}
                          fluid={{
                            ...item.image.fluid,
                            media: `(min-width: 0px)`,
                          }}
                          fadeIn={false}
                          backgroundColor={true}
                          loading={'eager'}
                          className={classes.image}
                        />
                        <Copy className="sm:text-lg text-base text-black-650 pt-3 leading-4">
                          {item.image.description}
                        </Copy>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </Swiper>
          </div>
        )}
        {isOverlayVisible && (
          <Lightbox className={classes.lightbox} onClick={handleCloseLightbox}>
            <LightboxContent
              style={(theme) => ({
                borderRadius: '5px',
                width: '80vw',
                maxWidth: '1200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',

                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                  maxWidth: '100%',
                },
              })}
              onClick={(e) => e.stopPropagation()}
            >
              {!isMobile && (
                <>
                  <div
                    className={clsx(classes.buttonBase, classes.buttonLeft)}
                    style={{ zIndex: 10 }}
                    onClick={() => handleButton(swiperRefOverlay, 'prev')}
                  >
                    <Icon name="Prev" />
                  </div>
                  <div
                    className={clsx(classes.buttonBase, classes.buttonRight)}
                    style={{ zIndex: 10 }}
                    onClick={() => handleButton(swiperRefOverlay, 'next')}
                  >
                    <Icon name="Next" />
                  </div>
                </>
              )}
              <div
                onClick={handleCloseLightbox}
                className="absolute z-50 top-0 right-2 m-2 text-black cursor-pointer"
              >
                <Icon name="Close" />
              </div>
              <div
                className="relative w-full h-full"
                style={{ height: '100%' }}
                ref={swiperRefOverlay}
              >
                <SwiperOverlay
                  slidesPerView="1"
                  loop={false}
                  initialSlide={selectedIndex}
                  onSlideChange={(swiper) =>
                    setSelectedIndex(swiper.activeIndex)
                  }
                  style={{ height: '100%', width: '100%' }}
                >
                  {items.map((item, i) => (
                    <div
                      key={`${item.created}${i}`}
                      className={classes.renderContainerOverlay}
                    >
                      {item.image && (
                        <Img
                          alt={item.image.description}
                          fluid={{
                            ...item.image.fluid,
                            media: `(min-width: 0px)`,
                          }}
                          fadeIn={false}
                          backgroundColor={true}
                          loading={'eager'}
                        />
                      )}
                      <Copy className="sm:text-lg text-base text-black-650 pt-3 leading-4">
                        {item.image.description}
                      </Copy>
                    </div>
                  ))}
                </SwiperOverlay>
              </div>
            </LightboxContent>
          </Lightbox>
        )}
      </div>
    </>
  )
}

ImageCarousel.propTypes = {
  className: PropTypes.string,
  headline: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.object,
    })
  ).isRequired,
}

export default ImageCarousel
